import React, { useState, useMemo, useEffect, useRef, memo } from "react";
import { calculateCellValue } from "../utils/formHelpers";
import { useDebounce } from "../hooks/useDebounce";

const DynamicTable = ({
  data,
  title,
  formTitle,
  isReportMode,
  columnOrder,
  allComponents,
  customColumns,
  projectDetails,

  remarksKey = "remarks",
  showRemarksColumn = false,
  onAddColumn,
  onDeleteColumn,
  onColumnDrop,
  onUpdateEntry,
  onDeleteRow,
  onExportExcel,
  onExportPDF,
  onLayoutChange,
  themeColor,
  onThemeColorChange,
  // --- REPORT MODE PROPS ---
  searchTerm,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  includeHeaderFooter,
  onToggleHeaderFooter,
  onToggleCreatedBy,
  onToggleCreatedDate,
  includeCreatedBy = true,
  includeCreatedDate = true,
  // --- LIST MODE PROPS ---
  onEdit,
  onDelete,
  onOpenOffcanvas,
  onSaveInline,
  onBatchSubmit,
  projectList = [],
  mixList = [],
  hasDrafts,
  selectedProject,
  submissionStatus,
}) => {
  // --- PERFORMANCE: Component is memoized to prevent unnecessary re-renders ---
  // --- State ---
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  // --- NEW: Track unsaved changes for manual save ---
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [headerFooterErrors, setHeaderFooterErrors] = useState({});
  const [showHeaderFooterValidation, setShowHeaderFooterValidation] = useState(false);

  // ========================================================================
  // LIST MODE STATE & HANDLERS (Only active when isReportMode === false)
  // ========================================================================
  const [listSearchTerm, setListSearchTerm] = useState("");
  const debouncedListSearch = useDebounce(listSearchTerm, 300);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [projectNameError, setProjectNameError] = useState("");
  const [mixNameError, setMixNameError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showAddRow, setShowAddRow] = useState(false);
  const [listCurrentPage, setListCurrentPage] = useState(1);
  const listItemsPerPage = 10;
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth > 992 : true);
  const [newEntryData, setNewEntryData] = useState({
    project_name: "",
    mix_name: "",
    created_date: new Date().toISOString().split("T")[0],
  });
  const [isCustomProject, setIsCustomProject] = useState(false);
  const [isCustomMix, setIsCustomMix] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setListCurrentPage(1);
  }, [debouncedListSearch]);

  // --- List mode handlers ---
  const cancelInlineEntry = () => {
    setNewEntryData({
      project_name: "",
      mix_name: "",
      created_date: new Date().toISOString().split("T")[0],
    });
    setShowAddRow(false);
    setIsCustomProject(false);
    setIsCustomMix(false);
    setProjectNameError("");
    setMixNameError("");
    setFieldErrors({});
  };

  const isValidEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const submitInlineEntry = (e) => {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
    if (!newEntryData.project_name || !newEntryData.mix_name) {
      setShowValidationModal(true);
      return false;
    }
    if (projectNameError || mixNameError) return false;
    const emailErrors = {};
    allComponents.forEach((comp) => {
      if (comp.type === "email" && newEntryData[comp.key]) {
        if (!isValidEmail(newEntryData[comp.key])) {
          emailErrors[comp.key] = "Please enter a valid email address";
        }
      }
    });
    if (Object.keys(emailErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...emailErrors }));
      return false;
    }
    try {
      if (onSaveInline) onSaveInline(newEntryData);
    } catch (error) {
      console.error("Error saving inline entry:", error);
    }
    // Preserve project_name and mix_name for the next entry
    const preservedProject = newEntryData.project_name;
    const preservedMix = newEntryData.mix_name;
    setNewEntryData({
      project_name: preservedProject,
      mix_name: preservedMix,
      created_date: new Date().toISOString().split("T")[0],
    });
    // Keep custom mode if user was in custom project/mix mode
    // setIsCustomProject(false);
    // setIsCustomMix(false);
    setProjectNameError("");
    setMixNameError("");
    setFieldErrors({});
    return false;
  };

  const handleEditClick = (row) => {
    const id = row.id || row._id || row._rowId;
    setEditingId(id);
    setEditFormData({ ...row });
  };
  const handleEditChange = (key, value) => {
    setEditFormData((prev) => ({ ...prev, [key]: value }));
  };
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };
  const handleSaveEdit = () => {
    if (onEdit) onEdit(editFormData);
    setEditingId(null);
  };
  const handleDeleteClick = (row) => {
    if (onDelete) onDelete(row);
  };
  const handleInlineChange = (key, value) => {
    setNewEntryData((prev) => ({ ...prev, [key]: value }));
  };

  // --- List mode computed values ---
  const listFilteredData = useMemo(() => {
    if (!debouncedListSearch) return data;
    const lowSearch = debouncedListSearch.toLowerCase();
    return data.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(lowSearch)
      )
    );
  }, [data, debouncedListSearch]);

  const listSortedData = useMemo(() => {
    return [...listFilteredData].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_date || 0);
      const dateB = new Date(b.createdAt || b.created_date || 0);
      if (dateB - dateA === 0) {
        return (b._rowId || b.id || 0) - (a._rowId || a.id || 0);
      }
      return dateB - dateA;
    });
  }, [listFilteredData]);

  const listStats = useMemo(() => {
    const lastEntry =
      listSortedData.length > 0
        ? (listSortedData[0].createdAt || listSortedData[0].created_date || "")
          .split("T")[0]
          .split("-")
          .reverse()
          .join("/")
        : "No entries";
    return { total: listFilteredData.length, lastDate: lastEntry };
  }, [listFilteredData, listSortedData]);

  const listIndexOfLastItem = listCurrentPage * listItemsPerPage;
  const listIndexOfFirstItem = listIndexOfLastItem - listItemsPerPage;
  const listCurrentData = listSortedData.slice(listIndexOfFirstItem, listIndexOfLastItem);
  const listTotalPages = Math.ceil(listFilteredData.length / listItemsPerPage);
  const listPaginate = (pageNumber) => setListCurrentPage(pageNumber);

  const listFontStyleText = { fontSize: "14px", color: "#374151" };
  const listHeaderStyle = { fontSize: "15px", color: "#111827", fontWeight: "700" };
  const listAccentColor = "#4F46E5";


  // --- Validate header/footer fields ---
  const validateHeaderFooterFields = () => {
    const errors = {};
    let isValid = true;

    // Validate headers: both label AND value must be filled
    localHeaders.forEach((item) => {
      const hasLabel = item.label && item.label.trim() !== '';
      const hasValue = item.value && item.value.trim() !== '';
      if (!hasLabel || !hasValue) {
        isValid = false;
        errors[`header_${item.id}`] = {
          label: !hasLabel,
          value: !hasValue,
        };
      }
    });

    // Validate footers: value must be filled (for text type)
    localFooters.forEach((item) => {
      if (item.type !== 'image') {
        const hasValue = item.value && item.value.trim() !== '';
        if (!hasValue) {
          isValid = false;
          errors[`footer_${item.id}`] = { value: true };
        }
      }
    });

    setHeaderFooterErrors(errors);
    setShowHeaderFooterValidation(!isValid);
    return isValid;
  };

  const handleSaveChanges = () => {
    if (!validateHeaderFooterFields()) return;
    if (onLayoutChange) {
      onLayoutChange({ headers: localHeaders, footers: localFooters });
    }
    setHasUnsavedChanges(false);
    setHeaderFooterErrors({});
    setShowHeaderFooterValidation(false);
  };

  // --- HEADER/FOOTER EDITING HANDLERS ---
  const addDetailField = (section) => {
    // Validate existing fields before adding a new one
    if (!validateHeaderFooterFields()) return;

    const newField = section === 'footers'
      ? { id: Date.now(), type: 'text', value: '' }
      : { id: Date.now(), label: '', value: '' };

    if (section === 'headers') {
      const updated = [...localHeaders, newField];
      setLocalHeaders(updated);
      setHasUnsavedChanges(true);
    } else {
      const updated = [...localFooters, newField];
      setLocalFooters(updated);
      setHasUnsavedChanges(true);
    }
  };

  const updateDetailField = (section, id, key, newValue) => {
    let updatedList;
    if (section === 'headers') {
      updatedList = localHeaders.map(item => item.id === id ? { ...item, [key]: newValue } : item);
      setLocalHeaders(updatedList);
      setHasUnsavedChanges(true);
      // Clear error for this field when user types
      if (newValue && newValue.trim() !== '') {
        setHeaderFooterErrors(prev => {
          const updated = { ...prev };
          if (updated[`header_${id}`]) {
            updated[`header_${id}`] = { ...updated[`header_${id}`], [key]: false };
            if (!updated[`header_${id}`].label && !updated[`header_${id}`].value) {
              delete updated[`header_${id}`];
            }
          }
          return updated;
        });
      }
    } else {
      updatedList = localFooters.map(item => item.id === id ? { ...item, [key]: newValue } : item);
      setLocalFooters(updatedList);
      setHasUnsavedChanges(true);
      // Clear error for this field when user types
      if (newValue && newValue.trim() !== '') {
        setHeaderFooterErrors(prev => {
          const updated = { ...prev };
          delete updated[`footer_${id}`];
          return updated;
        });
      }
    }
  };

  const deleteDetailField = (section, id) => {
    if (section === 'headers') {
      const updated = localHeaders.filter(item => item.id !== id);
      setLocalHeaders(updated);
      setHasUnsavedChanges(true);
    } else {
      const updated = localFooters.filter(item => item.id !== id);
      setLocalFooters(updated);
      setHasUnsavedChanges(true);
    }
  };

  const handleFooterImageUpload = (id, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => updateDetailField("footers", id, "value", reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Local state for Headers/Footers to allow immediate UI reordering
  const [localHeaders, setLocalHeaders] = useState([]);
  const [localFooters, setLocalFooters] = useState([]);

  // Dragging state for Headers/Footers
  const [draggedHeaderIndex, setDraggedHeaderIndex] = useState(null);
  const [draggedFooterIndex, setDraggedFooterIndex] = useState(null);

  const [columnToDelete, setColumnToDelete] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null); // { section: 'headers'|'footers', id: number }
  const colorInputRef = useRef(null);

  // Ref to track previous props to prevent unnecessary resets during parent re-renders
  const prevProjectDetailsRef = useRef(null);

  // --- Logic ---


  // --- Synchronization Effect ---
  useEffect(() => {
    // We compare stringified versions to avoid resetting local state
    // when the parent component re-renders but the data is effectively the same.
    // This fixes the "snap back" issue when dragging.
    const currentDetailsStr = JSON.stringify(projectDetails);
    const prevDetailsStr = prevProjectDetailsRef.current;

    if (currentDetailsStr !== prevDetailsStr) {
      const headers =
        projectDetails?.headers ||
        projectDetails?.project_details?.headers ||
        [];
      const footers =
        projectDetails?.footers ||
        projectDetails?.project_details?.footers ||
        [];

      if (Array.isArray(headers)) setLocalHeaders(headers);
      if (Array.isArray(footers)) setLocalFooters(footers);

      prevProjectDetailsRef.current = currentDetailsStr;
    }
  }, [projectDetails]);

  // --- Header Handlers (Project Details) ---
  const handleHeaderDragStart = (e, index) => {
    setDraggedHeaderIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Optional: Hide default ghost image
    // e.dataTransfer.setDragImage(e.target, 0, 0);
  };

  const handleHeaderDragEnter = (e, index) => {
    if (draggedHeaderIndex === null || draggedHeaderIndex === index) return;

    const newHeaders = [...localHeaders];
    const temp = newHeaders[draggedHeaderIndex];
    newHeaders[draggedHeaderIndex] = newHeaders[index];
    newHeaders[index] = temp;

    setLocalHeaders(newHeaders);
    setDraggedHeaderIndex(index);
  };

  const handleHeaderDragEnd = () => {
    setDraggedHeaderIndex(null);
    setHasUnsavedChanges(true);
  };

  const handleHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // --- Footer Handlers ---
  const handleFooterDragStart = (e, index) => {
    setDraggedFooterIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleFooterDragEnter = (e, index) => {
    if (draggedFooterIndex === null || draggedFooterIndex === index) return;

    const newFooters = [...localFooters];
    const temp = newFooters[draggedFooterIndex];
    newFooters[draggedFooterIndex] = newFooters[index];
    newFooters[index] = temp;

    setLocalFooters(newFooters);
    setDraggedFooterIndex(index);
  };

  const handleFooterDragEnd = () => {
    setDraggedFooterIndex(null);
    setHasUnsavedChanges(true);
  };

  const handleFooterDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // --- TYPOGRAPHY STYLES ---
  const fontText = { fontSize: "14px" };
  const fontHeading = { fontSize: "15px", fontWeight: "bold" };

  // Column Header Style
  const getHeaderStyle = (isDragging = false) => ({
    backgroundColor: isDragging ? "#e9ecef" : (themeColor || "#F9FAFB"),
    color: themeColor ? "#ffffff" : "#545757",
    border: "1px solid #dee2e6",
    cursor: "move",
    userSelect: "none",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: "bold",
  });

  const handleCellSave = (rowId, key, newValue) => {
    if (onUpdateEntry) onUpdateEntry(rowId, key, newValue);
    setEditingCell(null);
  };

  const formatDate = (val, isTime = false) => {
    if (!val) return "";
    const date = new Date(val);
    if (isNaN(date.getTime())) return val;
    if (isTime) {
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDisplayValue = (val) => {
    if (val === undefined || val === null || val === "") return "-";
    if (Array.isArray(val)) {
      if (val.length === 0) return "-";
      return val.join(", ");
    }
    if (typeof val === "object") return JSON.stringify(val);
    return val;
  };

  const renderDetailLine = (label, value) => (
    <div
      className="row mb-1 align-items-end"
      key={label}
      style={{ pointerEvents: "none" }}
    >
      <div className="col-5 text-muted" style={fontText}>
        {label}:
      </div>
      <div
        className="col-7 border-bottom position-relative"
        style={{ minHeight: "20px", borderColor: "#dee2e6" }}
      >
        {value && (
          <span
            className="fw-bold text-dark position-absolute bottom-0 start-0 ps-2"
            style={fontText}
          >
            {value}
          </span>
        )}
      </div>
    </div>
  );

  const getColumnLabel = (key, comp) => {
    if (key === "createdAt" || key === "created_date") return "Created Date";
    if (key === "createdBy" || key === "created_by") return "Created By";
    if (key === "updatedAt" || key === "updated_date") return "Created Date";
    return comp?.label || key;
  };

  const confirmDelete = () => {
    if (columnToDelete) {
      onDeleteColumn(columnToDelete);
      setColumnToDelete(null);
    }
  };

  const confirmDetailDelete = () => {
    if (itemToDelete) {
      deleteDetailField(itemToDelete.section, itemToDelete.id);
      setItemToDelete(null);
    }
  };

  const handleRowClick = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  return (
    <div className="bg-transparent" onClick={() => setColumnToDelete(null)}>
      <style>{`
        /* Custom Scrollbar from design */
        .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
        }
        .material-symbols-outlined {
            font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
            font-size: 20px; 
        }
        /* Sticky Actions Column */
        .sticky-action-col {
            position: sticky !important;
            right: 0 !important;
            z-index: 50;
            box-shadow: -2px 0 4px rgba(0,0,0,0.05);
            border-left: 1px solid #e2e8f0;
            width: 120px !important;
            min-width: 120px !important;
            max-width: 120px !important;
            opacity: 1 !important;
            /* overflow: hidden;  <-- REMOVED to prevent clipping */
            transform: translate3d(0,0,0); /* Force alignment/layering */
        }
        .sticky-action-white {
            background-color: #fff !important;
        }
        .sticky-action-blue {
            background-color: #f0f9ff !important;
        }
        .sticky-action-slate {
            background-color: #f1f5f9 !important;
        }
        :global(.dark) .sticky-action-white {
            background-color: #0f172a !important;
        }
        :global(.dark) .sticky-action-blue {
            background-color: #1e293b !important; 
        }
        :global(.dark) .sticky-action-slate {
            background-color: #1e293b !important;
        }
        /* Dark mode support check - if using tailwind 'dark:' classes, we might need a specific rule or just rely on the element classes if they utilize !important or high specificity. 
           However, let's make sure the background is solid. */
        :global(.dark) .sticky-action-col {
            background-color: #0f172a; /* slate-900 approx */
        }
        /* Higher z-index for header to stay on top of vertical scroll */
        thead .sticky-action-col {
            z-index: 30 !important;
        }
        /* Action button hover effects */
        .action-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            border: none;
            background: transparent;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        .action-btn:hover {
            transform: scale(1.15);
        }
        .action-btn:active {
            transform: scale(0.95);
        }
        .action-btn-edit {
            color: #4F46E5;
        }
        .action-btn-edit:hover {
            background-color: #EEF2FF;
            color: #3730A3;
            box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
        }
        .action-btn-delete {
            color: #EF4444;
        }
        .action-btn-delete:hover {
            background-color: #FEF2F2;
            color: #B91C1C;
            box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
        }
        .action-btn-save {
            color: #16A34A;
        }
        .action-btn-save:hover {
            background-color: #F0FDF4;
            color: #15803D;
            box-shadow: 0 2px 8px rgba(22, 163, 74, 0.2);
        }
        .action-btn-cancel {
            color: #6B7280;
        }
        .action-btn-cancel:hover {
            background-color: #F3F4F6;
            color: #374151;
            box-shadow: 0 2px 8px rgba(107, 114, 128, 0.2);
        }
      `}</style>
      {
        columnToDelete && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow-lg border-0 rounded-4">
                <div className="modal-body text-center p-4">
                  <div
                    className="mb-3 text-danger bg-danger-subtle rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="bi bi-trash3-fill fs-3"></i>
                  </div>
                  <h6 className="fw-bold mb-2">Delete Column?</h6>
                  <p className="text-muted small mb-4">
                    Are you sure you want to remove this column?
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-3 px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        setColumnToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm rounded-3 px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* --- NEW: Header/Footer Delete Confirmation Modal --- */}
      {
        itemToDelete && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow-lg border-0 rounded-4">
                <div className="modal-body text-center p-4">
                  <div
                    className="mb-3 text-danger bg-danger-subtle rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="bi bi-trash3-fill fs-3"></i>
                  </div>
                  <h6 className="fw-bold mb-2">Delete Item?</h6>
                  <p className="text-muted small mb-4">
                    Are you sure you want to remove this {itemToDelete.section === 'headers' ? 'header' : 'footer'} item?
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-3 px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm rounded-3 px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDetailDelete();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      <div className="report-header-section mb-3 position-relative">
        {/* --- CENTRAL NOTIFICATION TOAST --- */}
        {submissionStatus?.visible && (
          <div 
            className="position-fixed start-50 translate-middle-x animate-fade-in shadow-lg py-2 px-4 rounded-pill d-flex align-items-center gap-2"
            style={{ 
              top: "80px", 
              zIndex: 1060,
              backgroundColor: "#2c3e50",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
              minWidth: "max-content",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
            }}
          >
            <i className="bi bi-check-circle-fill text-success"></i>
            <span style={{ fontSize: "14px", fontWeight: "600" }}>{submissionStatus.message}</span>
          </div>
        )}
        {/* Added 'flex-wrap' and 'gap-2' to handle small screens gracefully */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          {/* Added 'flex-wrap' to the button group as well */}
          <div className="d-flex flex-wrap gap-2 align-items-center report-buttons-group">
            <button
              className="btn btn-success text-white btn-sm rounded-3 border-0 px-3 shadow-sm"
              onClick={onExportExcel}
              // ADDED: whiteSpace: 'nowrap' to prevent text breaking
              style={{
                backgroundColor: "#107c41",
                ...fontText,
                whiteSpace: "nowrap",
              }}
            >
              <i className="bi bi-file-earmark-excel me-1"></i> Export Excel
            </button>

            <button
              className="btn btn-danger text-white btn-sm rounded-3 border-0 px-3 shadow-sm"
              onClick={onExportPDF}
              // ADDED: whiteSpace: 'nowrap'
              style={{
                backgroundColor: "#dc2626",
                ...fontText,
                whiteSpace: "nowrap",
              }}
            >
              <i className="bi bi-file-earmark-pdf me-1"></i> Export PDF
            </button>

            <div>
              <button
                className="btn btn-light border btn-sm border text-muted shadow-sm"
                onClick={() => colorInputRef.current.click()}
                title="Change Theme Color"
                style={{
                  width: "34px",
                  height: "34px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="bi bi-palette-fill"></i>
              </button>
              <input
                ref={colorInputRef}
                type="color"
                value={themeColor}
                onChange={(e) => onThemeColorChange(e.target.value)}
                style={{ display: "none" }}
              />
            </div>

            {/* --- NEW: SEARCH INPUT --- */}
            <div
              className="d-flex align-items-center bg-white border rounded shadow-sm px-2"
              style={{ width: "250px", height: "31px" }}
            >
              <i className="bi bi-search text-muted"></i>
              <input
                type="text"
                className="form-control border-0 shadow-none bg-transparent outline-none py-0 ps-2"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{ fontSize: "14px" }}
              />
            </div>
          </div>

          <div className="report-buttons-group">
            <button
              className="btn btn-outline-primary btn-sm shadow-sm rounded-3 px-3 me-2"
              onClick={() => setShowPreviewModal(true)}
              style={fontText}
              title="Preview"
            >
              <i className="bi bi-eye"></i> <span className="d-none d-md-inline ms-1">Preview</span>
            </button>
          </div>
        </div>

        <div className="mb-2">
          <h5
            className="fw-bold text-dark m-0"
            style={{ ...fontHeading, fontSize: "1.1rem" }}
          >
            {formTitle || "Report"}
          </h5>
          <p className="text-secondary mb-0" style={{ fontSize: '12px', marginTop: '2px' }}>Form Data View & Entry Manager</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden mb-6">
        <div className="bg-slate-50 dark:bg-slate-800/40 px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between border-b border-slate-200 dark:border-slate-800 gap-3">
          <div className="flex items-start gap-3 w-full md:w-auto">
            <span className="material-symbols-outlined text-slate-400" style={{ marginTop: '2px' }}>settings</span>
            <span className="text-sm font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              Header & Footer Configuration
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2 md:mt-0">
            {hasUnsavedChanges && (
              <button
                className="text-xs font-bold text-primary bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                onClick={handleSaveChanges}
              >
                SAVE CHANGES
              </button>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Include in Export</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={includeHeaderFooter}
                  onChange={(e) => onToggleHeaderFooter(e.target.checked)}
                />
                <div className="w-6 h-3.5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-2.5 after:w-2.5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
              </label>
            </div>

            </div>
          </div>

        {/* Only show configuration inputs if enabled */}
        {includeHeaderFooter && (
          <div className="p-3 bg-white">
            {/* Validation Alert */}
            {showHeaderFooterValidation && Object.keys(headerFooterErrors).length > 0 && (
              <div className="alert alert-danger d-flex align-items-center py-2 px-3 mb-3" role="alert" style={{ fontSize: '13px' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <span>Please fill in all required fields (Label and Value for headers, Text for footers) before saving or adding new fields.</span>
                <button type="button" className="btn-close ms-auto" style={{ fontSize: '10px' }} onClick={() => setShowHeaderFooterValidation(false)}></button>
              </div>
            )}
            {/* --- HEADERS SECTION --- */}
            <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
              <h6 className="fw-bold small text-muted mb-0">Headers</h6>
              <button
                className="btn btn-sm btn-outline-primary py-0 px-2"
                onClick={() => addDetailField('headers')}
              >
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>

            <div className="row g-2 mb-4">
              {localHeaders.map((item, index) => (
                <div 
                  className="col-12 col-md-6" 
                  key={item.id || index}
                  onDragEnter={(e) => handleHeaderDragEnter(e, index)}
                  onDragOver={handleHeaderDragOver}
                  onDrop={handleHeaderDragEnd}
                >
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light cursor-grab" draggable onDragStart={(e) => handleHeaderDragStart(e, index)}>
                      <i className="bi bi-grip-vertical"></i>
                    </span>
                    <input
                      type="text"
                      className={`form-control ${headerFooterErrors[`header_${item.id}`]?.label ? 'is-invalid border-danger' : ''}`}
                      placeholder="Label *"
                      value={item.label}
                      onChange={(e) => updateDetailField('headers', item.id, 'label', e.target.value)}
                      title={headerFooterErrors[`header_${item.id}`]?.label ? 'Label is required' : ''}
                    />
                    <input
                      type="text"
                      className={`form-control ${headerFooterErrors[`header_${item.id}`]?.value ? 'is-invalid border-danger' : ''}`}
                      placeholder="Value *"
                      value={item.value}
                      onChange={(e) => updateDetailField('headers', item.id, 'value', e.target.value)}
                      title={headerFooterErrors[`header_${item.id}`]?.value ? 'Value is required' : ''}
                    />
                    <button className="btn btn-outline-danger" onClick={() => setItemToDelete({ section: 'headers', id: item.id })}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* --- FOOTERS SECTION --- */}
            <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
              <h6 className="fw-bold small text-muted mb-0">Footers</h6>
              <button
                className="btn btn-sm btn-outline-primary py-0 px-2"
                onClick={() => addDetailField('footers')}
              >
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>

            <div className="row g-2 mb-2">
              {localFooters.map((item, index) => (
                <div 
                  className="col-12 col-md-6" 
                  key={item.id || index}
                  onDragEnter={(e) => handleFooterDragEnter(e, index)}
                  onDragOver={handleFooterDragOver}
                  onDrop={handleFooterDragEnd}
                >
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light cursor-grab" draggable onDragStart={(e) => handleFooterDragStart(e, index)}>
                      <i className="bi bi-grip-vertical"></i>
                    </span>
                    <select
                      className="form-select"
                      style={{ maxWidth: '100px' }}
                      value={item.type} onChange={(e) => updateDetailField('footers', item.id, 'type', e.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                    </select>

                    {item.type === 'image' ? (
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => handleFooterImageUpload(item.id, e.target.files[0])}
                      />
                    ) : (
                      <input type="text" className={`form-control ${headerFooterErrors[`footer_${item.id}`]?.value ? 'is-invalid border-danger' : ''}`} placeholder="Footer Text *" value={item.value} onChange={(e) => updateDetailField('footers', item.id, 'value', e.target.value)} title={headerFooterErrors[`footer_${item.id}`]?.value ? 'Footer text is required' : ''} />
                    )}

                    <button className="btn btn-outline-danger" onClick={() => setItemToDelete({ section: 'footers', id: item.id })}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        {/* ADD COLUMN BUTTON FOR REPORT MODE */}
        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}
        </style>
        <div className="bg-light px-1 px-md-4 py-3 d-flex align-items-center justify-content-md-end border-bottom hide-scrollbar" style={{ overflowX: 'hidden', overflowY: 'visible', maxWidth: '100%' }}>
          <div className="d-flex flex-column flex-md-row align-items-end align-items-md-center gap-2" style={{ minWidth: 'min-content', width: '100%', justifyContent: 'flex-end' }}>
            <div className="d-flex flex-nowrap align-items-center gap-1 gap-md-2">
              <button
                className="btn btn-outline-primary btn-sm shadow-sm rounded-2 px-2 px-md-3 d-flex align-items-center justify-content-center"
                onClick={onAddColumn}
                style={{ fontSize: '13px', whiteSpace: 'nowrap', height: isDesktop ? '38px' : '32px' }}
              >
                <i className="bi bi-plus-lg"></i> <span className="ms-1">Add Column</span>
              </button>
              <button
                className={`btn btn-sm shadow-sm rounded-2 px-2 px-md-3 d-flex align-items-center gap-1 gap-md-2 ${isDesktop && showAddRow ? "btn-danger" : "btn-outline-primary"}`}
                onClick={() => {
                  if (isDesktop) {
                    setShowAddRow(!showAddRow);
                  } else {
                    onOpenOffcanvas && onOpenOffcanvas(null);
                  }
                }}
                style={{ fontSize: '13px', whiteSpace: 'nowrap', height: isDesktop ? '38px' : '32px' }}
              >
                <span className="material-symbols-outlined text-[16px]">
                  {isDesktop && showAddRow ? "close" : "post_add"}
                </span> 
                {isDesktop && showAddRow ? "Cancel Entry" : "New Entry"}
              </button>
            </div>
            {hasDrafts && (
              <button
                className="btn btn-sm text-white shadow-sm rounded-2 px-3 d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: listAccentColor, fontSize: '13px', whiteSpace: 'nowrap', height: isDesktop ? '38px' : '32px', minWidth: isDesktop ? 'auto' : '110px' }}
                onClick={() => onBatchSubmit && onBatchSubmit()}
              >
                <i className="bi bi-send"></i> Submit
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table
            className="table table-hover align-middle mb-0"
            style={{ minWidth: "100%" }}
          >
            <thead className="bg-light">
              <tr>
                <th
                  className="py-3 px-3 fw-bold text-secondary text-center align-middle border-bottom whitespace-nowrap"
                  style={{
                    cursor: "default",
                    width: "50px",
                    minWidth: "50px",
                  }}
                >
                  #
                </th>
                <th
                  className="py-3 px-3 fw-bold text-secondary text-center align-middle border-bottom whitespace-nowrap"
                  style={{
                    cursor: "default",
                    width: "150px",
                    minWidth: "150px",
                  }}
                >
                  Project Name
                </th>

                <th
                  className="py-3 px-3 fw-bold text-secondary text-center align-middle border-bottom whitespace-nowrap"
                  style={{
                    cursor: "default",
                    width: "150px",
                    minWidth: "150px",
                  }}
                >
                  Mix Name
                </th>

                {columnOrder.map((key) => {
                  const comp = allComponents.find((c) => c.key === key);
                  const isCustom = customColumns.find((c) => c.key === key);
                  let minW = "150px";
                  if (key.includes("Date") || key.includes("at"))
                    minW = "180px";
                  if (key.includes("By")) minW = "130px";

                  return (
                    <th
                      key={key}
                      className="py-3 px-3 fw-bold text-secondary text-center align-middle border-bottom position-relative group-hover whitespace-nowrap"
                      draggable={true}
                      onDragStart={() => setDraggedColumn(key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (onColumnDrop) {
                          onColumnDrop(draggedColumn, key);
                        }
                        setDraggedColumn(null);
                      }}
                      style={{
                        minWidth: minW,
                      }}
                    >
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <span className="text-truncate">{getColumnLabel(key, comp)}</span>
                        
                        {/* Ultra-Compact Toggle for Metadata Columns */}
                        {(key === "createdBy" || key === "created_by" || key === "createdAt" || key === "created_date") && (
                          <label className="relative inline-flex items-center cursor-pointer ms-1 flex-shrink-0" title={`${getColumnLabel(key, comp)} in Export`}>
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={key.includes("By") ? includeCreatedBy : includeCreatedDate}
                              onChange={(e) => key.includes("By") ? onToggleCreatedBy(e.target.checked) : onToggleCreatedDate(e.target.checked)}
                            />
                            <div className="w-6 h-3.5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-2.5 after:w-2.5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                          </label>
                        )}

                        {isCustom && (
                          <div
                            className="inline-flex items-center justify-center ml-1 text-red-500 bg-white rounded-full shadow-sm hover:bg-red-50"
                            title="Delete"
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setColumnToDelete(key);
                            }}
                          >
                            <span className="material-symbols-outlined text-[10px]">close</span>
                          </div>
                        )}
                        <span className="material-symbols-outlined text-slate-300 cursor-move text-[14px] opacity-0 group-hover:opacity-100 transition-opacity">drag_indicator</span>
                      </div>
                    </th>
                  );
                })}
                {/* Remarks Column Header */}
                {showRemarksColumn && (
                  <th
                    className="py-3 px-3 fw-bold text-secondary text-center align-middle border-bottom whitespace-nowrap"
                    style={{
                      cursor: "default",
                      width: "200px",
                      minWidth: "200px",
                    }}
                  >
                    Remarks
                  </th>
                )}
                <th
                  className="py-3 px-3 fw-bold text-secondary text-center action-cell whitespace-nowrap sticky-action-col bg-light border-bottom"
                  style={{
                    width: "120px",
                    minWidth: "120px",
                    position: "sticky",
                    right: 0,
                    zIndex: 20,
                    backgroundColor: "#f1f5f9", // mild slate-100 to match header bg
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody >
              {/* --- INLINE ENTRY ROW (Hide on Mobile or when toggled off) --- */}
              {isDesktop && showAddRow && (
                <tr className="table-light">
                  <td className="text-center text-primary fw-bold fs-5">+</td>
                  {/* Project Select / Create */}
                  <td className="p-1">
                    <div className="d-flex flex-column">
                      <div className="d-flex gap-1">
                        {isCustomProject ? (
                          <>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${projectNameError ? "is-invalid" : ""}`}
                              placeholder="New Project Name"
                              value={newEntryData.project_name}
                              onChange={(e) => {
                                const val = e.target.value;
                                handleInlineChange("project_name", val);
                                if (val.trim() && projectList.some((p) => p.toLowerCase() === val.trim().toLowerCase())) {
                                  setProjectNameError("This project name already exists");
                                } else {
                                  setProjectNameError("");
                                }
                              }}
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                              style={{ fontSize: "12px", minWidth: "120px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm p-0 d-flex align-items-center justify-content-center"
                              style={{ width: "24px", height: "30px", flexShrink: 0 }}
                              onClick={(e) => { e.preventDefault(); handleInlineChange("project_name", ""); setIsCustomProject(false); setProjectNameError(""); }}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <select
                              className="form-select form-select-sm"
                              value={newEntryData.project_name}
                              onChange={(e) => handleInlineChange("project_name", e.target.value)}
                              style={{ fontSize: "12px", minWidth: "120px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                            >
                              <option value="">Project...</option>
                              {projectList.map((p, i) => (<option key={i} value={p}>{p}</option>))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm p-0 d-flex align-items-center justify-content-center"
                              style={{ width: "24px", height: "30px" }}
                              onClick={(e) => { e.preventDefault(); setIsCustomProject(true); handleInlineChange("project_name", ""); setProjectNameError(""); }}
                            >
                              <i className="bi bi-plus-lg" style={{ fontSize: "14px" }}></i>
                            </button>
                          </>
                        )}
                      </div>
                      {projectNameError && (
                        <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px', whiteSpace: 'normal' }}>{projectNameError}</div>
                      )}
                    </div>
                  </td>
                  {/* Mix Select / Create */}
                  <td className="p-1">
                    <div className="d-flex flex-column">
                      <div className="d-flex gap-1">
                        {isCustomMix ? (
                          <>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${mixNameError ? "is-invalid" : ""}`}
                              placeholder="New Mix Name"
                              value={newEntryData.mix_name}
                              onChange={(e) => {
                                const val = e.target.value;
                                handleInlineChange("mix_name", val);
                                if (val.trim() && mixList.some((m) => m.toLowerCase() === val.trim().toLowerCase())) {
                                  setMixNameError("This mix name already exists");
                                } else {
                                  setMixNameError("");
                                }
                              }}
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                              style={{ fontSize: "12px", minWidth: "100px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm p-0 d-flex align-items-center justify-content-center"
                              style={{ width: "24px", height: "30px", flexShrink: 0 }}
                              onClick={(e) => { e.preventDefault(); handleInlineChange("mix_name", ""); setIsCustomMix(false); setMixNameError(""); }}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <select
                              className="form-select form-select-sm"
                              value={newEntryData.mix_name}
                              onChange={(e) => handleInlineChange("mix_name", e.target.value)}
                              style={{ fontSize: "12px", minWidth: "110px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                            >
                              <option value="">Mix...</option>
                              {mixList.map((m, i) => (<option key={i} value={m}>{m}</option>))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm p-0 d-flex align-items-center justify-content-center"
                              style={{ width: "24px", height: "30px" }}
                              onClick={(e) => { e.preventDefault(); setIsCustomMix(true); handleInlineChange("mix_name", ""); setMixNameError(""); }}
                            >
                              <i className="bi bi-plus-lg" style={{ fontSize: "14px" }}></i>
                            </button>
                          </>
                        )}
                      </div>
                      {mixNameError && (
                        <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px', whiteSpace: 'normal' }}>{mixNameError}</div>
                      )}
                    </div>
                  </td>

                  {/* Dynamic Inputs */}
                  {columnOrder.map((key) => {
                    const comp = allComponents.find((c) => c.key === key);
                    // Modify: Only render inputs for actual form components
                    // Skip system columns and custom columns ("Add Column")
                    const isCustom = customColumns?.some((c) => c.key === key);
                    const isSystemOrCustom = !comp || isCustom ||
                      ["createdBy", "createdAt", "updatedAt", "id", "status"].includes(key);

                    if (isSystemOrCustom) {
                      return (
                        <td key={key} className="p-1 text-center align-middle" style={{ background: "#f9fafb" }}>
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted fst-italic" style={{ fontSize: "11px", height: "30px" }}>
                            Auto-generated
                          </div>
                        </td>
                      );
                    }

                    const isEmail = comp?.type === "email";
                    return (
                      <td key={key} className="p-1">
                        <div className="d-flex flex-column">
                          {(() => {
                            const isSelect = comp?.type === "select" || comp?.type === "radio" || comp?.type === "checkbox";
                            const isDate = comp?.type === "date" || comp?.type === "datetime";
                            const isNumber = comp?.type === "number";
                            const isEmail = comp?.type === "email";

                            if (isSelect) {
                              const options = comp.values || comp.data?.values || [];
                              return (
                                <select
                                  className={`form-select form-select-sm ${fieldErrors[key] ? "is-invalid" : ""}`}
                                  value={newEntryData[key] || ""}
                                  onChange={(e) => {
                                    handleInlineChange(key, e.target.value);
                                    if (fieldErrors[key]) {
                                      setFieldErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
                                    }
                                  }}
                                  style={{ fontSize: "12px", minWidth: "100px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                                >
                                  <option value="">Select...</option>
                                  {options.map((opt, i) => (
                                    <option key={i} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              );
                            }

                            return (
                              <input
                                type={isDate ? "date" : isNumber ? "number" : isEmail ? "email" : "text"}
                                className={`form-control form-control-sm text-center ${fieldErrors[key] ? "is-invalid" : ""}`}
                                value={newEntryData[key] || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleInlineChange(key, val);
                                  if (isEmail) {
                                    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                                      setFieldErrors((prev) => ({ ...prev, [key]: "Enter a valid email" }));
                                    } else {
                                      setFieldErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
                                    }
                                  } else if (fieldErrors[key]) {
                                    setFieldErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (isNumber && ['e', 'E', '+', '-'].includes(e.key)) { e.preventDefault(); return; }
                                  if (e.key === "Enter") { e.preventDefault(); submitInlineEntry(e); }
                                }}
                                placeholder={comp?.label || key}
                                style={{ fontSize: "12px", minWidth: isDate ? "110px" : "70px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                              />
                            );
                          })()}
                          {fieldErrors[key] && (
                            <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px', whiteSpace: 'normal' }}>{fieldErrors[key]}</div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {showRemarksColumn && <td className="px-4 py-4"></td>}
                  {/* Action Buttons */}
                  {/* Action Buttons */}
                  <td className="text-center px-2 py-3 action-cell sticky-action-col bg-light border-bottom-0" style={{ width: "120px", minWidth: "120px", position: "sticky", right: 0, zIndex: 50, backgroundColor: "#f8fafc" }}>
                    <div className="flex gap-1 justify-center">
                      <button
                        type="button"
                        className="action-btn action-btn-save"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); submitInlineEntry(e); return false; }}
                        title="Save Entry"
                      >
                        <span className="material-symbols-outlined text-[18px]">check</span>
                      </button>
                      <button
                        type="button"
                        className="action-btn action-btn-delete"
                        onClick={(e) => { e.preventDefault(); cancelInlineEntry(); }}
                        title="Clear / Cancel"
                      >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {/* --- DATA ROWS --- */}
              {listCurrentData.map((row, idx) => {
                const isExpanded = expandedRowId === row._rowId;
                const isDraft = row.status === 0;
                const rowId = row.id || row._id || row._rowId;
                const isRowEditing = editingId === rowId;
                return (
                  <tr
                    key={row._id || row._rowId || idx}
                    className={isRowEditing ? "table-primary" : ""}
                    onClick={() => onOpenOffcanvas ? (isDesktop ? handleRowClick(row._rowId) : onOpenOffcanvas(row)) : handleRowClick(row._rowId)}
                    style={{ cursor: !isDesktop ? "pointer" : undefined }}
                  >
                    <td className={`${isRowEditing ? "px-4 py-1" : "px-4 py-3"} text-center align-middle text-dark`}>
                      {isDraft ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">DRAFT</span>
                      ) : (
                        listIndexOfFirstItem + idx + 1
                      )}
                    </td>
                    <td className={`${isRowEditing ? "px-4 py-1" : "px-4 py-3"} align-middle text-dark`}>
                      {isRowEditing ? (
                        <select
                          className="form-select form-select-sm w-100 shadow-none text-center"
                          value={editFormData.project_name || ""}
                          onChange={(e) =>
                            handleEditChange("project_name", e.target.value)
                          }
                          style={{ fontSize: "12px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                        >
                          <option value="">Select Project...</option>
                          {projectList.map((p, i) => (
                            <option key={i} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="w-full text-center">
                          <div className={isExpanded ? "whitespace-normal break-words" : "truncate max-w-[200px] mx-auto"}>
                            {row.project_name || "-"}
                          </div>
                        </div>
                      )}
                    </td>

                    <td className={`${isRowEditing ? "px-4 py-1" : "px-4 py-3"} text-center align-middle text-dark`}>
                      {isRowEditing ? (
                        <select
                          className="form-select form-select-sm w-100 shadow-none text-center"
                          value={editFormData.mix_name || ""}
                          onChange={(e) =>
                            handleEditChange("mix_name", e.target.value)
                          }
                          style={{ fontSize: "12px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                        >
                          <option value="">Select Mix...</option>
                          {mixList.map((m, i) => (
                            <option key={i} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="w-full text-center">
                          <div className={isExpanded ? "whitespace-normal break-words" : "truncate max-w-[200px] mx-auto"}>
                            {row.mix_name || "-"}
                          </div>
                        </div>
                      )}
                    </td>

                    {
                      columnOrder.map((key) => {
                        const comp = allComponents.find((c) => c.key === key);
                        const isCustom = customColumns.find((c) => c.key === key);
                        const isCalculated =
                          isCustom && isCustom.type === "calculated";
                        const isReadOnly =
                          key === "createdBy" ||
                          key === "createdAt" ||
                          key === "updatedAt" ||
                          key === "created_by" ||
                          key === "created_date" ||
                          key === "updated_date";
                        const isMetadataColumn =
                          isReadOnly ||
                          key.includes("Date") ||
                          key.includes("By");

                        let displayValue = calculateCellValue(
                          row,
                          isCustom || comp || { key },
                        );

                        if (
                          comp &&
                          (comp.type === "select" ||
                            comp.type === "radio" ||
                            comp.type === "checkbox")
                        ) {
                          const options = comp.values || comp.data?.values || [];
                          if (options.length > 0) {
                            if (Array.isArray(displayValue)) {
                              displayValue = displayValue.map((val) => {
                                const match = options.find(
                                  (opt) => opt.value === val,
                                );
                                return match ? match.label : val;
                              });
                            } else {
                              const match = options.find(
                                (opt) => opt.value === displayValue,
                              );
                              if (match) displayValue = match.label;
                            }
                          }
                        }

                        if (key === "createdAt" || key === "created_date")
                          displayValue = formatDate(displayValue, true);
                        if (key === "updatedAt" || key === "updated_date")
                          displayValue = formatDate(displayValue, true);
                        if (comp?.type === "date" || comp?.type === "datetime")
                          displayValue = formatDate(displayValue);

                        const isCellEditing =
                          editingCell?.rowId === row._rowId &&
                          editingCell?.colKey === key;
                        const isEditable =
                          !isReadOnly &&
                          (!isCustom || (isCustom && !isCalculated));

                        // Decide if we show the Row Edit input (Pencil) or Cell Edit input (Click)
                        const showRowInput = isRowEditing && isEditable;
                        const showCellInput = isCellEditing && isEditable && !isRowEditing;

                        return (
                          <td
                            key={key}
                            className={`${isRowEditing ? "px-4 py-1" : "px-4 py-3"} text-center align-middle text-dark ${isEditable && !isRowEditing && !isDesktop ? "cursor-pointer bg-light"
                              : ""
                              }`}
                            onClick={(e) => {
                              if (isEditable && !isCellEditing && !isRowEditing && !isDesktop) {
                                e.stopPropagation();
                                setEditingCell({
                                  rowId: row._rowId,
                                  colKey: key,
                                });
                              }
                            }}
                          >
                            {showRowInput ? (
                              (() => {
                                const isSelect = comp?.type === "select" || comp?.type === "radio" || comp?.type === "checkbox";
                                const isDate = comp?.type === "date" || comp?.type === "datetime";
                                const isNumber = comp?.type === "number";
                                const isEmail = comp?.type === "email";

                                if (isSelect) {
                                  const options = comp.values || comp.data?.values || [];
                                  return (
                                    <select
                                      className="form-select form-select-sm w-100 shadow-none text-center"
                                      value={editFormData[key] || ""}
                                      onChange={(e) => handleEditChange(key, e.target.value)}
                                      style={{ fontSize: "12px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                                    >
                                      <option value="">Select...</option>
                                      {options.map((opt, i) => (
                                        <option key={i} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  );
                                }

                                return (
                                  <input
                                    type={isDate ? "date" : isNumber ? "number" : isEmail ? "email" : "text"}
                                    className="form-control form-control-sm w-100 text-center shadow-none"
                                    value={editFormData[key] || ""}
                                    onChange={(e) => handleEditChange(key, e.target.value)}
                                    style={{ fontSize: "12px", height: "30px", paddingTop: "2px", paddingBottom: "2px" }}
                                    onKeyDown={(e) => {
                                      if (isNumber && ['e', 'E', '+', '-'].includes(e.key)) { e.preventDefault(); return; }
                                    }}
                                  />
                                );
                              })()
                            ) : showCellInput ? (
                              <input
                                autoFocus
                                type="text"
                                className="form-control form-control-sm w-100 text-center border-0 shadow-sm"
                                defaultValue={row[key] || ""}
                                onBlur={(e) =>
                                  handleCellSave(row._rowId, key, e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleCellSave(
                                      row._rowId,
                                      key,
                                      e.currentTarget.value,
                                    );
                                }}
                              />
                            ) : (
                              <div className="w-full text-center">
                                <div
                                  className={
                                    isExpanded
                                      ? "whitespace-normal break-words"
                                      : "truncate max-w-[200px] mx-auto"
                                  }
                                >
                                  {formatDisplayValue(displayValue)}
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })
                    }
                    {
                      showRemarksColumn && (
                        <td className={`${isRowEditing ? "px-4 py-1" : "px-4 py-3"} text-center align-middle text-dark`}>
                          <div className="w-full text-center">
                            <div
                              className={
                                isExpanded ? "whitespace-normal break-words" : "truncate max-w-[200px] mx-auto"
                              }
                            >
                              {row[remarksKey] || "-"}
                            </div>
                          </div>
                        </td>
                      )
                    }
                    <td className={`text-center px-2 ${isRowEditing ? "py-1" : "py-3"} action-cell sticky-action-col ${isRowEditing ? "bg-light" : "bg-white"} border-bottom-0`} onClick={(e) => e.stopPropagation()} style={{ width: "120px", minWidth: "120px", position: "sticky", right: 0, zIndex: 50 }}>
                      {isRowEditing ? (
                        <div className="flex gap-1 justify-center">
                          <button
                            className="action-btn action-btn-save"
                            onClick={handleSaveEdit}
                            title="Save"
                          >
                            <span className="material-symbols-outlined text-[18px]">check</span>
                          </button>
                          <button
                            className="action-btn action-btn-cancel"
                            onClick={handleCancelEdit}
                            title="Cancel"
                          >
                            <span className="material-symbols-outlined text-[18px]">close</span>
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-center">
                          <button
                            className="action-btn action-btn-edit"
                            onClick={() => handleEditClick(row)}
                            title="Edit"
                          >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                          </button>
                          <button
                            className="action-btn action-btn-delete"
                            onClick={() => handleDeleteClick(row)}
                            title="Delete"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- LIST MODE PAGINATION (If separate from below, logic seems duplicated in original file, but we keep this block as place holder or update it if it's the main one used) --- */}
      {/* Based on file structure, there were two pagination blocks. The second one (TargetContent above) seems to be the main one or for report mode. 
          The block at 1282 seems to be for 'list' mode. We should update BOTH. */ }
      {
        listTotalPages > 1 && (
          <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              Showing {((listCurrentPage - 1) * listItemsPerPage) + 1} to {Math.min(listCurrentPage * listItemsPerPage, data.length)} of {data.length} records
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-400"
                onClick={() => listPaginate(Math.max(1, listCurrentPage - 1))}
                disabled={listCurrentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: listTotalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => listPaginate(i + 1)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${listCurrentPage === i + 1 ? 'bg-primary text-white' : 'border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-400"
                onClick={() => listPaginate(Math.min(listTotalPages, listCurrentPage + 1))}
                disabled={listCurrentPage === listTotalPages}
              >
                Next
              </button>
            </div>
          </div>
        )
      }

      {/* --- VALIDATION MODAL --- */}
      {
        showValidationModal && (
          <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow-lg border-0 rounded-4">
                <div className="modal-body text-center p-4">
                  <div className="mb-3 text-warning bg-warning-subtle rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: "60px", height: "60px" }}>
                    <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "28px" }}></i>
                  </div>
                  <h6 className="fw-bold">Missing Required Fields</h6>
                  <p className="text-muted small mb-3">Please fill in both Project Name and Mix Name before saving.</p>
                  <button className="btn btn-sm btn-primary rounded-3 px-3" onClick={() => setShowValidationModal(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* --- REPORT MODE PAGINATION --- */}
      {
        totalPages > 1 && (
          <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between">
            <span className="text-xs text-slate-500" style={{ whiteSpace: 'nowrap' }}>
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-400"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => onPageChange(i + 1)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${currentPage === i + 1 ? 'bg-primary text-white' : 'border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-400"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )
      }

      <div className="mt-4 pb-3 px-2 d-flex flex-column gap-4">
        {localFooters.map((footer, index) => {
          const isSignature = footer.type === "image";
          return (
            <div
              className="d-block draggable-footer-item"
              key={footer.id || index}
              // --- UPDATED STYLE HERE (Fixes Underline Move) ---
              style={{
                width: isSignature ? "250px" : "100%",
                maxWidth: "100%",
                borderBottom: isSignature ? "1px solid black" : "none", // Moved here
                paddingBottom: "3px",
                cursor: "grab",
                opacity: draggedFooterIndex === index ? 0.4 : 1,
                transition: "all 0.2s ease",
                borderRadius: "4px",
              }}
              draggable={true}
              onDragStart={(e) => handleFooterDragStart(e, index)}
              onDragEnter={(e) => handleFooterDragEnter(e, index)} // <--- UPDATED
              onDragOver={handleFooterDragOver}
              onDragEnd={handleFooterDragEnd} // <--- UPDATED
            >
              {/* Inner div no longer has borderBottom */}
              <div
                className="ps-2 pt-1 position-relative"
                style={{ userSelect: "none", minHeight: "30px" }}
              >
                {isSignature && footer.value ? (
                  <img
                    src={footer.value}
                    alt="Signature"
                    style={{
                      height: "37px",
                      maxWidth: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <span className="fw-bold" style={fontText}>
                    {footer.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {
        showPreviewModal && (
          <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content shadow-lg border-0 rounded-4">
                <div className="modal-header border-bottom-0">
                  <h5 className="modal-title fw-bold">Report Preview</h5>
                  <button type="button" className="btn-close" onClick={() => setShowPreviewModal(false)}></button>
                </div>
                <div className="modal-body bg-light p-2 p-md-4">
                  <div className="bg-white shadow-sm p-3 mx-auto" style={{ width: "100%", minHeight: "100vh", fontFamily: "Arial, sans-serif" }}>
                    {/* Form Name */}
                    <div className="text-start mb-4">
                      <h2 className="fw-bold text-dark text-uppercase mb-0" style={{ letterSpacing: '1px' }}>{formTitle}</h2>
                    </div>

                    {/* Headers */}
                    {localHeaders.length > 0 && (
                      <div className="mb-4 border-bottom pb-3">
                        <div className="row g-3">
                          {localHeaders.map((h, i) => (
                            <div className="col-12 col-md-6" key={i}>
                              <div className="d-flex align-items-baseline">
                                <span className="fw-bold text-muted me-2 text-break" style={{ minWidth: '100px', flexShrink: 0 }}>{h.label}:</span>
                                <span className="fw-bold text-break">{h.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Table */}
                    <div className="table-responsive mb-4">
                      <table className="table table-bordered table-sm" style={{ width: '100%', tableLayout: 'auto' }}>
                        <thead style={{ backgroundColor: themeColor, color: 'white' }}>
                          <tr>
                            <th className="text-center p-2" style={{ width: '50px' }}>S.No</th>

                            <th className="text-center p-2">Mix Name</th>
                            {columnOrder.map(colKey => {
                              const comp = allComponents.find(c => c.key === colKey);
                              return <th key={colKey} className="text-center p-2">{getColumnLabel(colKey, comp)}</th>;
                            })}
                            {showRemarksColumn && <th className="text-center p-2">Remarks</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((row, idx) => (
                            <tr key={row._id || idx}>
                              <td className="text-center p-2">{idx + 1}</td>

                              <td className="text-center p-2">{row.mix_name || "-"}</td>
                              {columnOrder.map(colKey => {
                                const comp = allComponents.find(c => c.key === colKey);
                                let displayValue = calculateCellValue(row, comp || { key: colKey });
                                // Handle Select/Radio/Checkbox labels lookups
                                if (comp && (comp.type === "select" || comp.type === "radio" || comp.type === "checkbox")) {
                                  const options = comp.values || comp.data?.values || [];
                                  if (options.length > 0) {
                                    if (Array.isArray(displayValue)) {
                                      displayValue = displayValue.map((val) => {
                                        const match = options.find((opt) => opt.value === val);
                                        return match ? match.label : val;
                                      });
                                    } else {
                                      const match = options.find((opt) => opt.value === displayValue);
                                      if (match) displayValue = match.label;
                                    }
                                  }
                                }
                                // Formatting dates
                                if (colKey === "createdAt" || colKey === "created_date") displayValue = formatDate(displayValue, true);
                                if (colKey === "updatedAt" || colKey === "updated_date") displayValue = formatDate(displayValue, true);
                                if (comp?.type === "date" || comp?.type === "datetime") displayValue = formatDate(displayValue);

                                return (
                                  <td key={colKey} className="text-center p-2">
                                    {formatDisplayValue(displayValue)}
                                  </td>
                                );
                              })}
                              {showRemarksColumn && <td className="text-center p-2">{row[remarksKey] || "-"}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Footers */}
                    {localFooters.length > 0 && (
                      <div className="mt-4">
                        <div className="row g-3">
                          {localFooters.map((f, i) => (
                            <div key={i} className="col-12 text-start">
                              {f.type === 'image' && f.value ? (
                                <img src={f.value} alt="Signature" style={{ height: '50px', objectFit: 'contain', borderBottom: '1px solid #000', paddingBottom: '2px', display: 'inline-block' }} />
                              ) : (
                                <div className="fw-bold border-bottom border-dark pb-1 d-inline-block" style={{ minHeight: '20px' }}>{f.value}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default memo(DynamicTable);
